import {ServerRouter, type ServerRouterInstance} from "@/infrastructure/navigation";
import debounce from "debounce";
import {proxy} from "valtio";

import {IServerNavigable} from "@/shared/contracts";
import {Field, type FieldInstance} from "@/shared/models/form/fields";
import {type InfoCardProps} from "@/shared/ui/data-display/cards/info-card";
import {Tag} from "@/shared/ui/data-display/tags/tag-base";

import {BaseBlogsModelClass} from "../base-blogs.model";
import type {BlogsFilteredListProps} from "./blogs-filtered-list.view";

class BlogsFilteredListClass extends BaseBlogsModelClass implements IServerNavigable {
    // Config
    readonly tags: ReadonlyArray<Tag>;
    readonly serverRouter: ServerRouterInstance;

    // Archive-specific state
    activeTag?: string;
    search: FieldInstance;

    constructor(data: BlogsFilteredListProps) {
        super(data.popularPosts.pageSize ?? 8);

        this.tags = data.popularPosts.tags;
        this.posts = data.popularPosts.posts as Array<InfoCardProps>;
        this.activeTag = this.tags[0]?.value;
        this.hasMore = data.popularPosts.hasMore ?? false;
        this.serverRouter = ServerRouter();

        this.search = Field({initialValue: data.searchQuery});
    }

    init(): void {
        this.search.events.on(
            "change",
            debounce((value: string) => {
                this.isLoading = true;
                const params = new URLSearchParams(window.location.search);
                if (value) {
                    params.set("q", value);
                } else {
                    params.delete("q");
                }
                params.set("page", "1");

                this.serverRouter.replace(`?${params.toString()}`, {
                    scroll: false,
                    showProgressBar: false,
                });
            }, 500),
        );
    }

    update(data: BlogsFilteredListProps): void {
        this.updatePosts(data.popularPosts.posts as Array<InfoCardProps>, data.popularPosts.hasMore ?? false);
    }

    // Computed properties
    get filteredPosts(): Array<InfoCardProps> {
        if (!this.activeTag || this.activeTag === "All") return this.posts;
        return this.posts.filter((p) => p.category?.title === this.activeTag);
    }

    get visiblePosts(): Array<InfoCardProps> {
        return this.filteredPosts;
    }

    // Actions
    setActiveTag(tag: string): void {
        this.activeTag = tag;
    }

    loadMore(): void {
        this.isButtonLoading = true;
        const currentLimit = this.posts.length;
        const currentPage = Math.ceil(currentLimit / this.pageSize);
        const nextPage = currentPage + 1;

        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set("page", String(nextPage));
        searchParams.delete("limit");

        this.serverRouter.replace(`?${searchParams.toString()}`, {
            scroll: false,
            showProgressBar: false,
        });
    }

    override dispose(): void {
        this.serverRouter.dispose();
    }
}

export type BlogsFilteredListInstance = BlogsFilteredListClass;

export function createBlogsFilteredListModel(data: BlogsFilteredListProps): BlogsFilteredListInstance {
    return proxy(new BlogsFilteredListClass(data));
}
